import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  token: string | null;
  /** WMS-issued session JWT for username login (C12). Null when authed via Firebase. */
  wmsToken: string | null;
  /** WMS session id, paired with wmsToken. */
  wmsSessionId: string | null;
  /** WMS session expiry as ISO string (or null). */
  wmsTokenExpiresAt: string | null;
  login: (user: User, token: string) => void;
  loginWms: (
    user: User,
    token: string,
    sessionId: string,
    expiresAt: Date | string | null,
  ) => void;
  logout: () => void;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      token: null,
      wmsToken: null,
      wmsSessionId: null,
      wmsTokenExpiresAt: null,

      login: (user, token) =>
        set({
          user,
          token,
          isAuthenticated: true,
          wmsToken: null,
          wmsSessionId: null,
          wmsTokenExpiresAt: null,
        }),
      loginWms: (user, token, sessionId, expiresAt) =>
        set({
          user,
          isAuthenticated: true,
          token: null,
          wmsToken: token,
          wmsSessionId: sessionId,
          wmsTokenExpiresAt:
            expiresAt instanceof Date
              ? expiresAt.toISOString()
              : (expiresAt ?? null),
        }),
      logout: () =>
        set({
          user: null,
          token: null,
          wmsToken: null,
          wmsSessionId: null,
          wmsTokenExpiresAt: null,
          isAuthenticated: false,
        }),

      isAdmin: () => {
        const { user } = get();
        return !!user?.isAdmin;
      },
    }),
    {
      name: 'wms-auth',
      version: 2,
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Record<string, unknown>;
        if (version < 2 && state?.user && typeof state.user === 'object') {
          delete (state.user as Record<string, unknown>).role;
        }
        return state;
      },
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        wmsToken: state.wmsToken,
        wmsSessionId: state.wmsSessionId,
        wmsTokenExpiresAt: state.wmsTokenExpiresAt,
      }),
    },
  ),
);

// Dev mode mock login is now handled by AuthProvider.devLogin()
// and persisted via zustand persist middleware.
